import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, RefreshCw, Loader2, FileText, Upload, CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight, Truck, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getPostworksConfig, getPostworksItems, sendPostworks, refreshPostworksItem, deletePostworksItem, getPackFiles, getLegalDocPrefill } from "@/lib/api";

const POSTAGE = [
  { v: 2, label: "Second class", hint: "Standard — 2–3 working days" },
  { v: 1, label: "First class", hint: "Next working day" },
  { v: 3, label: "Priority", hint: "Fast-tracked print + 1st class" },
  { v: 4, label: "Eco", hint: "Cheapest, slowest" },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const statusTone = (it) => {
  if (it.ok === false) return "bg-rose-50 text-rose-700 border-rose-200";
  if (Number(it.status) === 5) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if ([6, 7, 8].includes(Number(it.status))) return "bg-rose-50 text-rose-700 border-rose-200";   // Postworks: 6 failed · 7 returned · 8 cancelled
  return "bg-amber-50 text-amber-800 border-amber-200";
};

export function PostageTab({ caseRow }) {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [refreshing, setRefreshing] = useState(null);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([getPostworksConfig(), getPostworksItems(caseRow.case_id)]);
      setStatus(s); setItems(r.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseRow.case_id]);

  const refresh = async (it) => {
    setRefreshing(it.id);
    try {
      const r = await refreshPostworksItem(it.id);
      setItems(prev => prev.map(x => x.id === it.id ? r.item : x));
      toast.success(`${it.file_name}: ${r.item.status_label}`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setRefreshing(null); }
  };

  return (
    <Card data-testid="postage-tab">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <Mail className="w-4 h-4 text-sky-600" />Postage
          <span className="text-xs font-normal text-muted-foreground">— letters printed &amp; posted by Postworks</span>
          {status && (
            <Badge variant="outline" title={status.api_error || status.base_url} className={cn("text-[10px]", !status.configured || status.api_ok === false ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200")} data-testid="postage-status-badge">
              {!status.configured ? "Postworks not configured" : status.api_ok === false ? "Postworks · not answering" : status.live ? "Postworks · live" : "Postworks · demo"}
            </Badge>
          )}
          <Button size="sm" className="ml-auto h-8 text-xs bg-sky-600 hover:bg-sky-700" onClick={() => setWizard(true)} disabled={!status?.configured || status?.api_ok === false} data-testid="postage-send-btn">
            <Send className="w-3.5 h-3.5 mr-1" />Send post
          </Button>
        </CardTitle>
        {status?.api_ok === false && (
          <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1" data-testid="postage-api-error">{status.api_error}</p>
        )}
        {status?.base_url_ignored && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="postage-base-url-ignored">
            Portal Settings → API → Postworks has “{status.base_url_ignored}” as the API base URL — that isn't a Postworks API host, so {status.base_url} is being used instead. Clear or correct the setting.
          </p>
        )}
      </CardHeader>
      <CardContent className="text-xs">
        {loading ? <div className="flex items-center gap-2 text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        : items.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center border rounded-md bg-slate-50/60" data-testid="postage-empty">
            Nothing posted from this file yet. <b>Send post</b> picks a PDF already on the file (or an upload), chooses postage, and Postworks prints and posts it — every status change lands on the timeline.
          </div>
        ) : (
          <div className="divide-y border rounded-md" data-testid="postage-items">
            {items.map(it => (
              <div key={it.id} className="p-2.5 flex items-start gap-3" data-testid={`postage-item-${it.id}`}>
                <FileText className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.file_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {it.recipient_name || it.pw_recipient || "Recipient from PDF"} · {it.postage_label}{it.is_colour ? " · colour" : ""}{it.is_single_sided ? " · single-sided" : ""} · sent {fmt(it.created_at)} by {it.created_by}
                    {it.cost != null && <> · £{Number(it.cost).toFixed(2)}</>}{it.page_count ? <> · {it.page_count} pp</> : null}
                  </div>
                  {it.est_delivery && <div className="text-[11px] text-muted-foreground flex items-center gap-1"><Truck className="w-3 h-3" />Est. delivery {fmt(it.est_delivery).slice(0, 11)}{it.royal_mail_handover ? ` · handed to Royal Mail ${fmt(it.royal_mail_handover)}` : ""}</div>}
                  {it.error && <div className="text-[11px] text-rose-700 break-words" data-testid={`postage-item-error-${it.id}`}>{String(it.error)}</div>}
                  {it.ok === false && it.postworks_response && (
                    <details className="text-[10px] text-muted-foreground" data-testid={`postage-item-raw-${it.id}`}>
                      <summary className="cursor-pointer">What Postworks actually sent back</summary>
                      <pre className="whitespace-pre-wrap break-all bg-slate-50 border rounded p-1.5 mt-1 max-h-40 overflow-auto">{JSON.stringify(it.postworks_response, null, 1).slice(0, 2000)}</pre>
                    </details>
                  )}
                  {it.urn && <div className="text-[10px] text-muted-foreground">URN {it.urn}</div>}
                </div>
                <Badge variant="outline" className={cn("text-[10px] shrink-0", statusTone(it))} data-testid={`postage-item-status-${it.id}`}>{it.status_label}</Badge>
                {it.ok === false && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 shrink-0 text-rose-600" title="Remove this failed attempt"
                    onClick={async () => { try { await deletePostworksItem(it.id); setItems(prev => prev.filter(x => x.id !== it.id)); } catch (e) { toast.error(e?.response?.data?.detail || e.message); } }}
                    data-testid={`postage-item-remove-${it.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
                {it.ok !== false && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 shrink-0" onClick={() => refresh(it)} disabled={refreshing === it.id} title="Check status with Postworks" data-testid={`postage-item-refresh-${it.id}`}>
                    <RefreshCw className={cn("w-3.5 h-3.5", refreshing === it.id && "animate-spin")} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {status && (
          <div className="text-[10px] text-muted-foreground mt-3">
            Status updates arrive by Postworks webhook → <code>{status.webhook_path}</code> and are written to the timeline. Postworks reads the recipient address from the PDF's top-left address window.
          </div>
        )}
      </CardContent>
      {wizard && <SendPostWizard caseRow={caseRow} onClose={() => setWizard(false)} onSent={() => { setWizard(false); load(); }} />}
    </Card>
  );
}

function SendPostWizard({ caseRow, onClose, onSent }) {
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState(null);
  const [selected, setSelected] = useState([]);
  const [upload, setUpload] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipient, setRecipient] = useState({ name: "", address: "" });
  const [opts, setOpts] = useState({ postage_type: 2, is_colour: false, is_single_sided: false, note: "" });
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await getPackFiles(caseRow.case_id);
        setFiles((r.files || []).filter(f => /\.pdf$/i.test(f.name || "") || /pdf/.test(f.mime_type || "")));
      } catch (e) { toast.error(e?.response?.data?.detail || e.message); setFiles([]); }
      try {
        const p = await getLegalDocPrefill(caseRow.case_id, "lba");
        setRecipients(p.recipients || []);
      } catch { /* recipients optional */ }
    })();
  }, [caseRow.case_id]);

  const visible = useMemo(() => (files || []).filter(f => !filter || (f.name || "").toLowerCase().includes(filter.toLowerCase())), [files, filter]);
  const toggle = (key) => setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const chosen = (files || []).filter(f => selected.includes(f.key));
  const onUpload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/pdf$/i.test(f.name) && f.type !== "application/pdf") return toast.error("Postworks only accepts PDF letters");
    const rd = new FileReader(); rd.onload = () => setUpload({ name: f.name, base64: String(rd.result).split(",")[1] }); rd.readAsDataURL(f);
  };
  const canNext = step === 0 ? (selected.length > 0 || upload) : true;

  const send = async () => {
    setSending(true);
    try {
      const r = await sendPostworks(caseRow.case_id, {
        file_keys: selected, upload_base64: upload?.base64, upload_name: upload?.name,
        recipient_name: recipient.name, recipient_address: recipient.address, ...opts,
        created_by: JSON.parse(localStorage.getItem("sf_user") || "{}").name || "Ops",
      });
      if (r.ok) toast.success(`${r.items.filter(i => i.ok).length} letter(s) sent to Postworks${r.failed.length ? ` · ${r.failed.length} failed` : ""}`);
      else toast.error(r.failed?.[0]?.error || "Postworks rejected the letter");
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSending(false); }
  };

  const STEPS = ["Choose letters", "Recipient & postage", "Review & send"];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" aria-describedby={undefined} data-testid="postage-wizard">
        <DialogHeader className="min-w-0">
          <DialogTitle className="flex items-center gap-2 min-w-0 pr-8">
            <Mail className="w-4 h-4 text-sky-600 shrink-0" /><span className="truncate">Send post — {caseRow.customer_name}</span>
          </DialogTitle>
          <div className="text-xs text-muted-foreground pt-1" data-testid="postage-step">Step {step + 1} of 3 — {STEPS[step]}</div>
          <div className="flex gap-1.5">{STEPS.map((_, i) => <div key={i} className={cn("h-1 flex-1 rounded", i <= step ? "bg-sky-600" : "bg-slate-200")} />)}</div>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-3">
            <div className="text-xs text-slate-700 bg-sky-50 border border-sky-200 rounded-md p-3 leading-relaxed">
              Pick the PDF(s) to post — each becomes its own letter. Postworks reads the recipient's address from the <b>top-left address block on page 1</b>, so only choose letters that carry one (PDN, DN, LBA, SD, court covers all do).
            </div>
            <Input placeholder="Filter files…" value={filter} onChange={e => setFilter(e.target.value)} className="h-8 text-xs" data-testid="postage-file-filter" />
            {files === null ? <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading the Files tab…</div> : (
              <div className="border rounded-md max-h-72 overflow-y-auto divide-y" data-testid="postage-file-list">
                {visible.length === 0 && <div className="p-3 text-xs text-muted-foreground">No PDFs match.</div>}
                {visible.map(f => (
                  <label key={f.key} className="flex items-center gap-2 p-2 text-xs cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={selected.includes(f.key)} onChange={() => toggle(f.key)} data-testid={`postage-file-${f.key}`} />
                    <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{f.source}{f.last_modified ? ` · ${fmt(f.last_modified).slice(0, 11)}` : ""}</span>
                  </label>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 text-xs border rounded-md p-2 cursor-pointer hover:bg-slate-50">
              <Upload className="w-3.5 h-3.5 text-slate-500" />
              <span className="flex-1">{upload ? <>Upload: <b>{upload.name}</b></> : "…or upload a one-off PDF"}</span>
              <input type="file" accept="application/pdf" className="hidden" onChange={onUpload} data-testid="postage-upload-input" />
              {upload && <button type="button" className="text-rose-600 underline" onClick={(e) => { e.preventDefault(); setUpload(null); }}>remove</button>}
            </label>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <div className="text-[11px] font-medium text-slate-700 mb-1">Recipient (for the timeline — the PDF's address window is what Postworks uses)</div>
              <div className="flex gap-2">
                <Input value={recipient.name} onChange={e => setRecipient(r => ({ ...r, name: e.target.value }))} placeholder="Name" className="h-8 text-xs flex-1" data-testid="postage-recipient-name" />
                {recipients.length > 0 && (
                  <select className="h-8 text-xs border rounded px-1 bg-white w-[45%] min-w-0" value="" onChange={e => { const r = recipients[Number(e.target.value)]; if (r) setRecipient({ name: r.name, address: r.address || "" }); }} data-testid="postage-recipient-picker">
                    <option value="">Use contact from file…</option>
                    {recipients.map((r, i) => <option key={i} value={i}>{r.name} · {r.role}</option>)}
                  </select>
                )}
              </div>
              <Textarea value={recipient.address} onChange={e => setRecipient(r => ({ ...r, address: e.target.value }))} placeholder="Address (optional, for the record)" className="text-xs mt-1.5 min-h-[56px]" data-testid="postage-recipient-address" />
            </div>
            <div>
              <div className="text-[11px] font-medium text-slate-700 mb-1">Postage</div>
              <div className="grid grid-cols-2 gap-2">
                {POSTAGE.map(p => (
                  <button key={p.v} type="button" onClick={() => setOpts(o => ({ ...o, postage_type: p.v }))}
                    className={cn("text-left border rounded-md p-2.5 text-xs", opts.postage_type === p.v ? "border-sky-500 bg-sky-50 ring-1 ring-sky-300" : "hover:bg-slate-50")} data-testid={`postage-class-${p.v}`}>
                    <div className="font-medium">{p.label}</div><div className="text-[10px] text-muted-foreground">{p.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="flex items-center gap-2 border rounded-md p-2 cursor-pointer"><input type="checkbox" checked={opts.is_colour} onChange={e => setOpts(o => ({ ...o, is_colour: e.target.checked }))} data-testid="postage-colour" />Print in colour</label>
              <label className="flex items-center gap-2 border rounded-md p-2 cursor-pointer"><input type="checkbox" checked={opts.is_single_sided} onChange={e => setOpts(o => ({ ...o, is_single_sided: e.target.checked }))} data-testid="postage-single-sided" />Single-sided (default is double-sided)</label>
            </div>
            <Textarea value={opts.note} onChange={e => setOpts(o => ({ ...o, note: e.target.value }))} placeholder="Note for the timeline (optional)" className="text-xs min-h-[48px]" data-testid="postage-note" />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2 text-xs" data-testid="postage-review">
            <div className="border rounded-md p-3 space-y-1.5 bg-slate-50">
              <div className="font-medium">Letters ({chosen.length + (upload ? 1 : 0)})</div>
              {chosen.map(f => <div key={f.key} className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-slate-500" />{f.name}</div>)}
              {upload && <div className="flex items-center gap-2"><Upload className="w-3.5 h-3.5 text-slate-500" />{upload.name}</div>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="border rounded-md p-2.5"><div className="text-muted-foreground">Recipient</div><div className="font-medium">{recipient.name || "— from the PDF —"}</div><div className="text-[11px] whitespace-pre-line">{recipient.address}</div></div>
              <div className="border rounded-md p-2.5"><div className="text-muted-foreground">Postage</div><div className="font-medium">{POSTAGE.find(p => p.v === opts.postage_type)?.label}</div><div className="text-[11px]">{opts.is_colour ? "Colour" : "Black & white"} · {opts.is_single_sided ? "single-sided" : "double-sided"}</div></div>
            </div>
            <div className="flex items-start gap-2 text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2.5"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />This sends real post and is billed to the Morley Capital Postworks account. A "Posted via Postworks" note goes on the timeline; status updates follow via webhook.</div>
          </div>
        )}

        <DialogFooter className="gap-1">
          <Button variant="ghost" onClick={onClose} data-testid="postage-cancel">Cancel</Button>
          {step > 0 && <Button variant="outline" onClick={() => setStep(s => s - 1)} data-testid="postage-back"><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>}
          {step < 2 ? (
            <Button onClick={() => setStep(s => s + 1)} disabled={!canNext} data-testid="postage-next">Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={send} disabled={sending} className="bg-sky-600 hover:bg-sky-700" data-testid="postage-confirm-send">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}{sending ? "Sending…" : "Send to Postworks"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
